/* eslint-disable @typescript-eslint/no-non-null-assertion */
import { Injector } from '@angular/core';

import { AbstractControl, FormArray, FormControl, FormGroup } from '@angular/forms';
import { Router } from '@angular/router';
import { setNoMatchReasonFromResponse } from '@app/finxone-web-frontend/app/Modules/shared/widgets/add-beneficiary-v2/beneficiary-v2.utils';
import {
  SetCopResponseStateMatchStatusCode,
  SetCopResponseStateName,
} from '@app/finxone-web-frontend/app/lib/actions/cop-response.action';
import { AccountService } from '@app/finxone-web-frontend/app/lib/services/account-service/account-service.service';
import {
  AddBeneficiaryRequest,
  CheckPayeeResponse,
  Payee,
  UpdateBeneficiaryRequest,
} from '@app/finxone-web-frontend/app/lib/services/account-service/account.type';
import { WorkflowFormsService } from '@app/finxone-web-frontend/app/lib/services/workflow-forms/workflow-forms.service';
import { addEditBeneficiary } from '@app/finxone-web-frontend/app/lib/utils/cta-button-actions/domestic-payments/add-beneficiary';
import { createRedirectionUrlFromNavigationOptions } from '@app/finxone-web-frontend/app/lib/utils/utils';
import { FormID, OtpParam } from '@finxone-platform/form-action';
import { AlertHandlerService } from '@finxone-platform/shared/services';
import {
  ActionButtonOutcomesType,
  ActionButtonWidgetActionsOutcomesNavigationConfigMap,
  BaseWidgetProperties,
  ButtonActionType,
  CheckPayeeMatchStatus,
} from '@finxone-platform/shared/sys-config-types';
import { Store } from '@ngxs/store';

function markAllControlsDirtyAndTouched(control: AbstractControl) {
  if (control instanceof FormGroup || control instanceof FormArray) {
    Object.values(control.controls).forEach((childControl) => {
      markAllControlsDirtyAndTouched(childControl);
    });
  }
  control.markAsTouched();
  control.markAsDirty();
}

export function onBeneficiaryBankCredentialsConfirm(
  injector: Injector,
  widgetProperties: BaseWidgetProperties,
): void {
  const router = injector.get(Router);
  const navigationConfig = widgetProperties?.actionNavigationConfigurations;
  const workflowFormService = injector.get(WorkflowFormsService);
  const beneficiaryForm = workflowFormService.getFormPart(FormID.BENEFICIARY_FORM);

  // validate all controls
  markAllControlsDirtyAndTouched(beneficiaryForm?.get('accountDetails') as FormGroup);

  const defaultNavigation = ActionButtonWidgetActionsOutcomesNavigationConfigMap.get(
    ButtonActionType.BENEFICIARY_FORM_BANK_PROCEED,
  )!;
  const successNavigation =
    navigationConfig?.[ActionButtonOutcomesType.SUCCESS] ?? defaultNavigation.success!;
  const processingNavigation =
    navigationConfig?.[ActionButtonOutcomesType.PROCESSING] ?? defaultNavigation.processing!;

  if (beneficiaryForm?.get('accountDetails')?.valid && beneficiaryForm?.get('beneficiaryAddress')?.valid) {
    // If both parts are valid, then we don't need any more credentials
    const redirectUrl = createRedirectionUrlFromNavigationOptions(successNavigation);
    router.navigateByUrl(redirectUrl);
  } else if (
    beneficiaryForm?.get('accountDetails')?.valid &&
    beneficiaryForm?.get('beneficiaryAddress')?.invalid
  ) {
    // if Address is invalid but accountDetails is valid, then Address is required so redirect
    const redirectUrl = createRedirectionUrlFromNavigationOptions(processingNavigation);
    router.navigateByUrl(redirectUrl);
  } else {
    // If both accountDetails and beneficiaryAddress are invalid, we can handle it as needed
  }
}

export function onBeneficiaryAddressCredentialsConfirm(
  injector: Injector,
  widgetProperties: BaseWidgetProperties,
): void {
  const router = injector.get(Router);
  const navigationConfig = widgetProperties?.actionNavigationConfigurations;
  const workflowFormService = injector.get(WorkflowFormsService);
  const beneficiaryForm = workflowFormService.getFormPart(FormID.BENEFICIARY_FORM);

  // validate all controls
  markAllControlsDirtyAndTouched(beneficiaryForm?.get('beneficiaryAddress') as FormGroup);

  const defaultNavigation = ActionButtonWidgetActionsOutcomesNavigationConfigMap.get(
    ButtonActionType.BENEFICIARY_FORM_ADDRESS_PROCEED,
  )!;
  const successNavigation =
    navigationConfig?.[ActionButtonOutcomesType.SUCCESS] ?? defaultNavigation.success!;
  const processingNavigation =
    navigationConfig?.[ActionButtonOutcomesType.PROCESSING] ?? defaultNavigation.processing!;

  if (beneficiaryForm?.get('accountDetails')?.valid && beneficiaryForm?.get('beneficiaryAddress')?.valid) {
    // If both parts are valid, then we don't need any more credentials
    const redirectUrl = createRedirectionUrlFromNavigationOptions(successNavigation);
    router.navigateByUrl(redirectUrl);
  } else if (
    beneficiaryForm?.get('beneficiaryAddress')?.valid &&
    beneficiaryForm?.get('accountDetails')?.invalid
  ) {
    // if Account Details is invalid but beneficiaryAddress is valid, then Account Details is required so redirect
    const redirectUrl = createRedirectionUrlFromNavigationOptions(processingNavigation);
    router.navigateByUrl(redirectUrl);
  } else {
    // If both accountDetails and beneficiaryAddress are invalid, we can handle it as needed
  }
}

export function beneficiaryFormCopCheck(injector: Injector, widgetProperties: BaseWidgetProperties): void {
  const accountService = injector.get(AccountService);
  const alertHandlerService = injector.get(AlertHandlerService);
  const workflowFormService = injector.get(WorkflowFormsService);
  const beneficiaryForm = workflowFormService.getFormPart(FormID.BENEFICIARY_FORM);
  const store = injector.get(Store);
  const router = injector.get(Router);
  const navigationConfig = widgetProperties?.actionNavigationConfigurations;

  if (!beneficiaryForm?.valid) {
    alertHandlerService.showAlertFn('error', 'Please make sure all the necessary details are provided.');
    return;
  }

  const currency = beneficiaryForm?.get('currency')?.value;
  const country = beneficiaryForm?.get('country')?.value;
  let checkPayeeRequest: Payee;
  if (
    currency === 'GBP' &&
    country === 'GB' &&
    beneficiaryForm?.get('accountDetails.sortCode')?.value &&
    beneficiaryForm?.get('accountDetails.accountNumber')?.value
  ) {
    checkPayeeRequest = {
      name: beneficiaryForm?.get('accountDetails.fullName')?.value,
      accountNumber: beneficiaryForm?.get('accountDetails.accountNumber')?.value,
      sortCode: beneficiaryForm?.get('accountDetails.sortCode')?.value.replace(/-/g, '').trim(),
      accountType: beneficiaryForm?.get('type')?.value,
      srd: beneficiaryForm?.get('srd')?.value,
    };
  } else if (
    currency === 'EUR' &&
    beneficiaryForm?.get('paymentRail')?.value !== 'sepa-target-2' &&
    beneficiaryForm?.get('paymentRail')?.value !== 'swift' &&
    beneficiaryForm?.get('accountDetails.iban')?.value
  ) {
    checkPayeeRequest = {
      name: beneficiaryForm?.get('accountDetails.fullName')?.value,
      iban: beneficiaryForm?.get('accountDetails.iban')?.value,
      accountType: beneficiaryForm?.get('type')?.value,
    };
  } else {
    // Not supported currency for COP or VOP check, so just submit the form
    addEditBeneficiary(injector, widgetProperties, true);
    return;
  }

  const param = OtpParam.AddBeneficiary;

  const defaultNavigation = ActionButtonWidgetActionsOutcomesNavigationConfigMap.get(
    ButtonActionType.BENEFICIARY_FORM_COP,
  )!;
  const successNavigation =
    navigationConfig?.[ActionButtonOutcomesType.SUCCESS] ?? defaultNavigation.success!;
  const processingNavigation =
    navigationConfig?.[ActionButtonOutcomesType.PROCESSING] ?? defaultNavigation.processing!;
  const failureNavigation =
    navigationConfig?.[ActionButtonOutcomesType.FAILURE] ?? defaultNavigation.failure!;
  const incompleteNavigation =
    navigationConfig?.[ActionButtonOutcomesType.INCOMPLETE] ?? defaultNavigation.incomplete!;

  const redirectUrlOnMatch = createRedirectionUrlFromNavigationOptions(successNavigation);
  const redirectUrlOnCloseMatch = createRedirectionUrlFromNavigationOptions(processingNavigation);
  const redirectUrlOnNoMatch = createRedirectionUrlFromNavigationOptions(failureNavigation);
  const redirectUrlOnSecondaryIdentifier = createRedirectionUrlFromNavigationOptions(incompleteNavigation);

  accountService
    .checkPayee(
      {
        ...checkPayeeRequest,
      } as Payee,
      param,
    )
    .subscribe((checkPayeeResponse: CheckPayeeResponse) => {
      store.dispatch(new SetCopResponseStateName(checkPayeeResponse.name));
      store.dispatch(new SetCopResponseStateMatchStatusCode(checkPayeeResponse.matchStatusCode));

      if (checkPayeeResponse.matchStatusCode === CheckPayeeMatchStatus.INTERNAL) {
        beneficiaryForm.addControl('copStatus', new FormControl(checkPayeeResponse.matchStatusCode));
        addEditBeneficiary(injector, widgetProperties, true);
      } else if (checkPayeeResponse.matchStatusCode == CheckPayeeMatchStatus.MATCHED) {
        //success
        const redirectUrl = redirectUrlOnMatch + `?flow=${param}`;
        router.navigateByUrl(redirectUrl);
      } else if (
        checkPayeeResponse.matchStatusCode == CheckPayeeMatchStatus.CLOSE_MATCH ||
        checkPayeeResponse.matchStatusCode == CheckPayeeMatchStatus.NAME_MATCHED ||
        checkPayeeResponse.matchStatusCode == CheckPayeeMatchStatus.ACCOUNT_TYPE_MISMATCH_NAME_CLOSE_MATCH
      ) {
        // pending
        const redirectUrl = redirectUrlOnCloseMatch + `?flow=${param}`;
        router.navigateByUrl(redirectUrl);
      } else {
        if (
          checkPayeeResponse.matchStatusCode == CheckPayeeMatchStatus.INVALID_SECONDARY_IDENTIFIER &&
          !checkPayeeRequest.srd
        ) {
          // secondary identifier required (this was an old requirement, and no longer in use for now)
          const redirectUrl = redirectUrlOnSecondaryIdentifier + `?flow=${param}`;
          router.navigateByUrl(redirectUrl);
        } else {
          // failure
          setNoMatchReasonFromResponse(checkPayeeResponse.matchStatusCode, store);
          const redirectUrl = redirectUrlOnNoMatch + `?flow=${param}`;
          router.navigateByUrl(redirectUrl);
        }
      }
    });
}

export function mapBeneficiaryFormDataToAddBeneficiaryRequest(
  beneficiaryForm: FormGroup<any>,
  isAddressTouched: boolean | undefined,
): AddBeneficiaryRequest | UpdateBeneficiaryRequest {
  const formValue = beneficiaryForm.value;
  return {
    name: formValue.accountDetails.fullName || undefined,
    accountType: formValue.type,
    copStatus: formValue.copStatus || undefined,
    destinationIdentifier: {
      accountNumber: formValue.accountDetails.accountNumber || undefined,
      sortCode: formValue.accountDetails.sortCode || undefined,
      country: formValue.country,
      currency: formValue.currency,
      iban: formValue.accountDetails.iban || undefined,
      bic: formValue.accountDetails.BIC || undefined,
      aba: formValue.accountDetails.aba || undefined,
      rtnCanada: formValue.accountDetails.rtnCanada || undefined,
      clabe: formValue.accountDetails.clabe || undefined,
      bsbCode: formValue.accountDetails.bsbCode || undefined,
      ifsc: formValue.accountDetails.ifsc || undefined,
      cnaps: formValue.accountDetails.cnaps || undefined,
      bankId: formValue.accountDetails.bankId || undefined,
      bankName: formValue.accountDetails.bankName || undefined,
      bankCode: formValue.accountDetails.bankCode || undefined,
      branchCode: formValue.accountDetails.branchCode || undefined,
    },
    address:
      formValue.beneficiaryAddress && isAddressTouched
        ? {
            addressLine1: formValue.beneficiaryAddress.addressLine1,
            addressLine2: formValue.beneficiaryAddress.addressLine2 || undefined,
            addressLine3: formValue.beneficiaryAddress.addressLine3 || undefined,
            addressLine4: formValue.beneficiaryAddress.addressLine4 || undefined,
            city: formValue.beneficiaryAddress.city,
            state: formValue.beneficiaryAddress.stateProvinceRegion || undefined,
            country: formValue.beneficiaryAddress.country,
            postCode: formValue.beneficiaryAddress.postalCode || undefined,
          }
        : undefined,
    id: formValue.id || undefined,
  };
}
